import type { EscrowCreation } from 'src/types/chain';

/**
 * How many wallet prompts the rental pay dialog promises, and what its button says while the api
 * works (api#380, PR-5). Pure, so the decision is a unit test and the dialog only renders it.
 *
 * Three prompts (create the escrow, approve, pay) when the renter's own wallet creates the contract:
 * known from the sheet once `prepare-payment` has answered, from a creation this browser already
 * sent (the stash), or — before either — from the chain registry saying `buyer`. Two when the
 * contract exists or the registry says `platform`. Against an api older than the field the dialog
 * hedges until the sheet arrives, EVM only (TRON is always the platform's two).
 */
export interface RentalPromptInput {
  isEvm: boolean;
  /** The clone's address once known — a deployed contract is always two prompts. */
  contractAddress: string;
  /** What the dialog promised when the run started, kept until it ends; null outside a run. */
  runThreePrompts: boolean | null;
  hasSheet: boolean;
  hasStashedCreation: boolean;
  /** The deal chain's registry entry; undefined while the registry has not loaded or predates the field. */
  escrowCreation: EscrowCreation | undefined;
}

export interface RentalPromptPlan {
  /** The run's own count: three when a sheet or a stashed creation is in hand. */
  threePrompts: boolean;
  /** What the dialog says before the first prompt: three, by the sheet, the stash or the registry. */
  promisedThree: boolean;
  /** Neither two nor three can be promised yet: an EVM booking against a registry that does not say. */
  promptsUnknown: boolean;
  /** The button while `prepare-payment` runs: the api signs a sheet, or deploys (TRON, older api). */
  preparingLabel: 'Preparing escrow…' | 'Creating the deal contract…';
}

export const rentalPromptPlan = (input: RentalPromptInput): RentalPromptPlan => {
  const { isEvm, contractAddress, runThreePrompts, hasSheet, hasStashedCreation, escrowCreation } = input;
  const noContract = !contractAddress;
  const threePrompts = runThreePrompts ?? (noContract && (hasSheet || hasStashedCreation));
  const promisedThree = threePrompts || (isEvm && noContract && escrowCreation === 'buyer');
  const promptsUnknown = noContract && !promisedThree && isEvm && escrowCreation === undefined;

  return {
    threePrompts,
    promisedThree,
    promptsUnknown,
    preparingLabel: escrowCreation === 'buyer' ? 'Preparing escrow…' : 'Creating the deal contract…',
  };
};
